import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api.js';
import PageHeader from '../../components/PageHeader.jsx';
import IconSelect from '../../components/IconSelect.jsx';
import { Icon, SECTION_COLOR } from '../../icons.jsx';
import CreateLoad from '../dispatch/CreateLoad.jsx';
import Pagination from '../../components/Pagination.jsx';

const TOOLBAR_ACTIONS = [
  { label: 'Bulk import', icon: Icon.upload, disabled: true },
  { label: 'Route & mileage', icon: Icon.route, disabled: true },
  { label: 'Columns', icon: Icon.columns, disabled: true }
];

// Two-letter initials from a customer name — "Ch Robinson Inc" -> "CR",
// "Any Customer" -> "AC" — for the small avatar badge next to Customer Name.
function customerInitials(name) {
  if (!name) return '—';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

// A wide, mostly-distinct palette so different customers rarely land on the
// same color — picked deterministically from a hash of the name, so the
// same customer always gets the same color across the whole list.
const AVATAR_COLORS = [
  '#4C8FE0', '#3FBF6F', '#F2A93B', '#E0475E', '#9B84E8', '#2DC4C4',
  '#E86E96', '#C57709', '#5B8C5A', '#B5548A', '#3670B3', '#D9822B',
  '#6B7280', '#0E9F6E', '#7E3AF2', '#D63384',
];
function customerColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

const LOAD_TYPE_COLORS = { FTL: '#4C8FE0', LTL: '#F2A93B' };
const CURRENCY_COLORS = { CAD: '#3FBF6F', USD: '#7E3AF2' };

export default function Orders() {
  const [loads, setLoads] = useState(null);
  const [billingStage, setBillingStage] = useState('on_board');
  const [page, setPage] = useState(1);
  // Rows-per-page isn't a fixed number — it's however many rows actually
  // fit below the table header before the viewport runs out, so page 1
  // never needs an internal scrollbar. Two mechanisms work together:
  // recalcRowsPerPage() makes a fresh estimate (on new data / window
  // resize) using the tallest currently-rendered row, then a separate
  // post-render safety net (below) measures the real rendered bottom edge
  // and trims by one row at a time until there's provably zero overflow —
  // that second pass is what makes this exact rather than approximate,
  // since it doesn't need to know every margin/padding/border in the
  // chain above the table, it just checks the real result.
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Bumped on every recalcRowsPerPage() pass (see below) so the overflow
  // safety-net effect always re-verifies after a resize, even on the
  // (common) case where the fresh estimate happens to land on the same
  // rowsPerPage number as before.
  const [measureTick, setMeasureTick] = useState(0);
  const tableWrapRef = useRef(null);
  const footerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(searchParams.get('new') === '1');
  const [editLoadId, setEditLoadId] = useState(null);
  const [rowActionMenu, setRowActionMenu] = useState(null); // { loadId, top, left } or null
  const [showSummary, setShowSummary] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);

  const [loadType, setLoadType] = useState('');
  const [createdDate, setCreatedDate] = useState('');
  const [byDays, setByDays] = useState('');
  const [byStatus, setByStatus] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [fields, setFields] = useState({ loadNo: '', customerOrderNo: '', tripNo: '', customer: '', pickup: '', delivery: '', ref: '' });
  const [advanced, setAdvanced] = useState({ dateFrom: '', dateTo: '', customer: '', pickup: '', delivery: '', orderNo: '' });

  function load() {
    setLoads(null);
    api(`/dispatch/loads?billingStage=${billingStage}`).then(setLoads).catch(() => setLoads([]));
  }

  const deleteLoad = async (l) => {
    if (!window.confirm(`Delete order ${l.load_number}? This can't be undone.`)) return;
    setRowActionMenu(null);
    try {
      await api(`/dispatch/loads/${l.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const createInvoice = async (l) => {
    setRowActionMenu(null);
    try {
      const result = await api(`/accounting/invoices/from-load/${l.id}`, { method: 'POST' });
      alert(`Invoice ${result.invoiceNumber} created.`);
    } catch (e) {
      alert(e.message);
    }
  };

  // "Recur Order" — duplicates this order as a brand new one (today's date,
  // new load number), reusing the same customer/shipments/stops/revenue.
  const recurOrder = async (l) => {
    setRowActionMenu(null);
    try {
      const full = await api(`/dispatch/loads/${l.id}`);
      const payload = {
        customerId: full.customer_id || null,
        customerOrderNumber: full.customer_order_number, loadType: full.load_type, freightOn: full.freight_on,
        salesmanId: full.salesman_id || null,
        commissionType: full.commission_type, commissionValue: full.commission_value,
        orderNotes: full.order_notes, currency: full.currency,
        scaleTicket: full.scale_ticket, shipmentType: full.shipment_type, destination: full.destination,
        isCsa: !!full.is_csa, isCtpat: !!full.is_ctpat, isPip: !!full.is_pip,
        isHazmat: !!full.is_hazmat, isTarp: !!full.is_tarp, isOverDimension: !!full.is_over_dimension,
        shipments: full.shipments.map(s => ({
          commodity: s.commodity, valueOfGoods: s.value_of_goods, valueCurrency: s.value_currency,
          equipment: s.equipment, numPackages: s.num_packages, packageUnit: s.package_unit,
          weight: s.weight, weightUnit: s.weight_unit, temperature: s.temperature, tempUnit: s.temp_unit,
          dimL: s.dim_l, dimW: s.dim_w, dimH: s.dim_h, dimUnit: s.dim_unit,
        })),
        stops: full.stops.map(s => ({
          stopType: s.stop_type, isYard: !!s.is_yard, locationId: s.location_id, locationName: s.location_name || s.location,
          referenceNo: s.reference_no, date: new Date().toISOString().slice(0, 10), time: s.stop_time,
          appointment: !!s.appointment, note: s.note, loadingOption: s.loading_option,
          contactName: s.contact_name, contactPhone: s.contact_phone,
          isPartial: !!s.is_partial, partialQuantity: s.partial_quantity, partialUnit: s.partial_unit,
          partialWeight: s.partial_weight, partialWeightUnit: s.partial_weight_unit,
          tripNumber: s.trip_number,
        })),
        revenueItems: full.revenue.map(r => ({ revenueItem: r.revenue_item, rateMethod: r.rate_method, rate: r.rate, value: r.value })),
        taxes: full.taxes.map(t => ({ rate: t.tax_rate, amount: t.tax_amount, basedOn: t.taxes_based_on })),
      };
      await api('/dispatch/loads', { method: 'POST', body: JSON.stringify(payload) });
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  useEffect(() => { load(); setPage(1); }, [billingStage]);

  // Fresh estimate — uses the TALLEST currently-rendered row (not the
  // first), since rows are variable height (the credit-limit line only
  // renders on some rows) and using a short row's height to size the page
  // is exactly what caused the previous overshoot: it fit more short rows
  // than the actual mix of row heights allowed. The footer reservation
  // uses the last real measured footer height once we have one, so repeat
  // estimates (e.g. on resize) get more accurate over time; the constant
  // is only a rough starting point for the very first measurement, before
  // the exact overflow-check pass (below) trims any remaining error.
  const recalcRowsPerPage = useCallback(() => {
    const wrap = tableWrapRef.current;
    if (!wrap) return;
    const thead = wrap.querySelector('thead');
    const rows = wrap.querySelectorAll('tbody tr');
    if (!thead || !rows.length) return;
    let maxRowHeight = 0;
    rows.forEach(r => { const h = r.getBoundingClientRect().height; if (h > maxRowHeight) maxRowHeight = h; });
    if (!maxRowHeight) return;
    const tbodyTop = thead.getBoundingClientRect().bottom;
    const footerHeight = footerRef.current ? footerRef.current.getBoundingClientRect().height : 70;
    const available = window.innerHeight - tbodyTop - footerHeight;
    const estimate = Math.max(1, Math.floor(available / maxRowHeight));
    setRowsPerPage(prev => (prev === estimate ? prev : estimate));
    // Bump unconditionally, even when the estimate matches the existing
    // value — an unchanged estimate can still be wrong for the new size
    // (e.g. it rounds to the same number that no longer fits), and without
    // this the safety-net effect below would never re-fire to catch that,
    // since its dependency (rowsPerPage) wouldn't have actually changed.
    setMeasureTick(t => t + 1);
  }, []);

  // Fresh estimate only on new data and on window resize (debounced) — NOT
  // on every rowsPerPage/page change, otherwise it would fight the
  // decrement-only safety net below (that one only ever shrinks; growth
  // back up only ever comes from here, so the two can't oscillate).
  useLayoutEffect(() => {
    recalcRowsPerPage();
  }, [loads, recalcRowsPerPage]);

  useEffect(() => {
    let t;
    const onResize = () => { clearTimeout(t); t = setTimeout(recalcRowsPerPage, 150); };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); clearTimeout(t); };
  }, [recalcRowsPerPage]);

  // Exact safety net — after every render, check the one signal that
  // directly means "the page needs to scroll": document scrollHeight vs
  // clientHeight. Measuring the footer's own bottom edge against
  // window.innerHeight (an earlier version of this) looked right but
  // missed .main's own bottom padding, which sits after the whole flex
  // row and isn't part of the footer's box — the footer could measure as
  // "fitting" while the page still overflowed by exactly that padding.
  // scrollHeight vs clientHeight sidesteps that entirely: it doesn't
  // matter what padding/margin/border/sibling contributes the extra
  // height, if the document is taller than the viewport this is true.
  // If it overflows, trim one row and let this effect re-verify against
  // the new render; repeats until it fits exactly or hits 1 row.
  useLayoutEffect(() => {
    if (!footerRef.current) return;
    const overflow = document.documentElement.scrollHeight > document.documentElement.clientHeight + 0.5;
    if (overflow && rowsPerPage > 1) setRowsPerPage(n => Math.max(1, n - 1));
  }, [rowsPerPage, page, loads, measureTick]);

  // Defensive clamp — if a reload or a rowsPerPage recalculation (e.g. the
  // window got taller/shorter) leaves the current page past the new last
  // page, snap back into range instead of showing an empty page.
  useEffect(() => {
    if (!loads) return;
    const totalPages = Math.max(1, Math.ceil(loads.length / rowsPerPage));
    if (page > totalPages) setPage(totalPages);
  }, [loads, rowsPerPage]);

  useEffect(() => {
    if (searchParams.get('new') === '1') setShowForm(true);
  }, [searchParams]);

  function resetFields() {
    setFields({ loadNo: '', customerOrderNo: '', tripNo: '', customer: '', pickup: '', delivery: '', ref: '' });
  }

  function resetAdvanced() {
    setAdvanced({ dateFrom: '', dateTo: '', customer: '', pickup: '', delivery: '', orderNo: '' });
  }

  const counts = {
    assigned: loads?.filter(l => l.status === 'assigned').length ?? 0,
    inTransit: loads?.filter(l => l.status === 'in_transit').length ?? 0,
    delivered: loads?.filter(l => l.status === 'delivered').length ?? 0,
    unassigned: loads?.filter(l => l.status === 'unassigned').length ?? 0
  };

  const totalPages = Math.max(1, Math.ceil((loads?.length ?? 0) / rowsPerPage));
  const pageRows = (loads || []).slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <div>
      {(showForm || editLoadId) && (
        <div className="order-form-modal-overlay" onClick={() => { setShowForm(false); setEditLoadId(null); setSearchParams({}); }}>
          <div className="order-form-modal" onClick={e => e.stopPropagation()}>
            <CreateLoad
              loadId={editLoadId}
              title={editLoadId ? 'Edit order' : 'New order'}
              onSaved={() => { setShowForm(false); setEditLoadId(null); setSearchParams({}); load(); }}
              onCancel={() => { setShowForm(false); setEditLoadId(null); setSearchParams({}); }}
            />
          </div>
        </div>
      )}
      {/* Header stays full-width and fixed regardless of the drawer —
          only the body content below adjusts to make room for it. */}
      <PageHeader
        icon={Icon.orders}
        title="Orders"
        subtitle="Every load, across all statuses"
        color={SECTION_COLOR.orders}
        actions={
          <>
            <button onClick={() => setShowForm(true)}>+ New order</button>
            {TOOLBAR_ACTIONS.map(a => (
              <button key={a.label} className="toolbar-btn" disabled={a.disabled} title={a.disabled ? `${a.label} — coming soon` : a.label}>
                <a.icon />
                <span>{a.label}</span>
                {a.disabled && <span className="nav-flyout-soon">Soon</span>}
              </button>
            ))}
            <button className="topbar-icon-btn" title="Summary" onClick={() => setShowSummary(s => !s)}><Icon.summary /></button>
            <button className="topbar-icon-btn" title="Filter"><Icon.filter /></button>
            <button className={'topbar-icon-btn' + (showDrawer ? ' active' : '')} title="Search" onClick={() => setShowDrawer(s => !s)}><Icon.search /></button>
          </>
        }
      />

      {showSummary && (
        <div className="status-summary">
          <span className="status-summary-label"><Icon.summary /> Status summary</span>
          <span className="status-summary-item" style={{color:'var(--amber)'}}>Assigned: {counts.assigned}</span>
          <span className="status-summary-item" style={{color:'var(--blue)'}}>In transit: {counts.inTransit}</span>
          <span className="status-summary-item" style={{color:'var(--green)'}}>Delivered: {counts.delivered}</span>
          <span className="status-summary-item" style={{color:'var(--red)'}}>Unassigned: {counts.unassigned}</span>
        </div>
      )}

      {/* Only this row resizes — content column flexes, drawer's width
          animates smoothly via CSS transition instead of appearing instantly. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Billing stage tabs */}
          <div className="billing-stage-tabs">
            {[
              { key: 'on_board',        label: 'On Board' },
              { key: 'complete',        label: 'Complete' },
              { key: 'invoice_customer',label: 'Invoice Customer' },
              { key: 'invoice_factor',  label: 'Invoice Factor' },
              { key: 'factor_paid',     label: 'Factor Paid' },
              { key: 'paid',            label: 'Paid' },
            ].map(tab => (
              <button key={tab.key} type="button"
                className={'billing-stage-tab' + (billingStage === tab.key ? ' active' : '')}
                onClick={() => setBillingStage(tab.key)}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Panel 1 — dropdown filters, its own strip */}
          <div className="panel filter-row-panel">
            <div className="filter-row">
              <IconSelect icon={Icon.list} value={loadType} onChange={e => setLoadType(e.target.value)}>
                <option value="">Load type</option>
                <option value="dry_van">Dry van</option>
                <option value="reefer">Reefer</option>
                <option value="flatbed">Flatbed</option>
              </IconSelect>
              <IconSelect icon={Icon.calendar} value={createdDate} onChange={e => setCreatedDate(e.target.value)}>
                <option value="">Created date</option>
                <option value="today">Today</option>
                <option value="week">This week</option>
                <option value="month">This month</option>
              </IconSelect>
              <IconSelect icon={Icon.calendar} value={byDays} onChange={e => setByDays(e.target.value)}>
                <option value="">By days</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </IconSelect>
              <IconSelect icon={Icon.sort} value={byStatus} onChange={e => setByStatus(e.target.value)}>
                <option value="">By status</option>
                <option value="unassigned">Unassigned</option>
                <option value="assigned">Assigned</option>
                <option value="in_transit">In transit</option>
                <option value="delivered">Delivered</option>
              </IconSelect>
              <IconSelect icon={Icon.sort} value={sortBy} onChange={e => setSortBy(e.target.value)}>
                <option value="">Sort by</option>
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
                <option value="rate">Rate</option>
              </IconSelect>
              <button className="topbar-icon-btn" title="Columns"><Icon.columns /></button>
            </div>
          </div>

          {/* Panel 2 — field filters, fields and Search/Reset all in one row together */}
          <div className="panel filter-fields-panel">
            <div className="filter-fields-row">
              <label>Load #<br/>
                <input value={fields.loadNo} onChange={e => setFields(f => ({...f, loadNo: e.target.value}))} placeholder="Load No" />
              </label>
              <label>Customer order #<br/>
                <input value={fields.customerOrderNo} onChange={e => setFields(f => ({...f, customerOrderNo: e.target.value}))} placeholder="Customer Order No" />
              </label>
              <label>Trip #<br/>
                <input value={fields.tripNo} onChange={e => setFields(f => ({...f, tripNo: e.target.value}))} placeholder="Trip No" />
              </label>
              <label>Customer<br/>
                <select value={fields.customer} onChange={e => setFields(f => ({...f, customer: e.target.value}))}>
                  <option value="">Select</option>
                </select>
              </label>
              <label>Pickup location<br/>
                <select value={fields.pickup} onChange={e => setFields(f => ({...f, pickup: e.target.value}))}>
                  <option value="">Select</option>
                </select>
              </label>
              <label>Delivery location<br/>
                <select value={fields.delivery} onChange={e => setFields(f => ({...f, delivery: e.target.value}))}>
                  <option value="">Select</option>
                </select>
              </label>
              <label>Pickup/delivery ref #<br/>
                <input value={fields.ref} onChange={e => setFields(f => ({...f, ref: e.target.value}))} placeholder="Reference No" />
              </label>
              <button className="primary-btn"><Icon.search /> Search</button>
              <button onClick={resetFields}>Reset</button>
            </div>
          </div>

          {loads === null && <div className="empty">Loading...</div>}
          {loads && loads.length === 0 && <div className="empty">No orders yet — click "+ New order" above.</div>}
          {loads && loads.length > 0 && (
          <div className="orders-table-wrap" ref={tableWrapRef}>
            <table className="orders-table">
              <colgroup>
                <col style={{ width: 50 }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: 84 }} />
              </colgroup>
              <thead>
                <tr>
                  <th></th>
                  <th>Order # / Cust. Order #</th>
                  <th>Trip #</th>
                  <th>Customer Name</th>
                  <th>Pickup Date / Location / Ref #</th>
                  <th>Delivery Date / Location / Ref #</th>
                  <th>Rate / Credit / Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <button type="button" className="row-action-dots-circle"
                        onClick={e => {
                          e.stopPropagation();
                          const r = e.currentTarget.getBoundingClientRect();
                          setRowActionMenu(rowActionMenu?.loadId === l.id ? null : { loadId: l.id, top: r.bottom + 4, left: r.left });
                        }}
                        title="Actions">⋮</button>
                      {rowActionMenu?.loadId === l.id && createPortal(
                        <>
                          <div className="trip-action-backdrop" onClick={() => setRowActionMenu(null)} />
                          <div className="trip-action-menu" style={{ position: 'fixed', top: rowActionMenu.top, left: rowActionMenu.left, right: 'auto' }}>
                            <button type="button" onClick={() => createInvoice(l)}>Create Invoice</button>
                            <button type="button" onClick={() => recurOrder(l)}>Recur Order</button>
                            <button type="button" disabled title="Not built yet.">Pending Items</button>
                            <div className="action-menu-divider" />
                            {[
                              { key: 'on_board',         label: '→ On Board' },
                              { key: 'complete',         label: '→ Complete' },
                              { key: 'invoice_customer', label: '→ Invoice Customer' },
                              { key: 'invoice_factor',   label: '→ Invoice Factor' },
                              { key: 'factor_paid',      label: '→ Factor Paid' },
                              { key: 'paid',             label: '→ Paid' },
                            ].filter(s => s.key !== l.billing_stage).map(s => (
                              <button key={s.key} type="button" onClick={async () => {
                                await api(`/dispatch/loads/${l.id}/billing-stage`, { method: 'PATCH', body: JSON.stringify({ billingStage: s.key }) });
                                setRowActionMenu(null);
                                load();
                              }}>{s.label}</button>
                            ))}
                          </div>
                        </>,
                        document.body
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--cl-blue, #4C8FE0)', cursor: 'pointer' }} onClick={() => setEditLoadId(l.id)}>
                        {l.load_number}
                      </div>
                      <span className="load-type-badge" style={{ background: LOAD_TYPE_COLORS[l.load_type] || '#6B7280' }}>{l.load_type}</span>
                      <div style={{ fontSize: 12, color: '#9B84E8', fontWeight: 600, marginTop: 4 }}>{l.customer_order_number || '—'}</div>
                    </td>
                    <td>
                      <div>{l.trip_id || '—'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-dim)', opacity: .75 }}>
                        <span style={{ color: 'var(--cl-blue, #4C8FE0)', fontWeight: 600 }}>{l.pickup_count || 0}P</span>
                        {' / '}
                        <span style={{ color: 'var(--cl-green, #3FBF6F)', fontWeight: 600 }}>{l.delivery_count || 0}D</span>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="company-initial-badge" style={{ background: customerColor(l.customer_name) }} title={l.customer_name || ''}>{customerInitials(l.customer_name)}</div>
                        <div>
                          <div style={{ fontWeight: 700 }}>{l.customer_name || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-dim)', opacity: .75 }}>
                            {l.created_at ? new Date(l.created_at).toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' }) : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {l.first_pickup_location ? (
                        <>
                          <div>{l.first_pickup_location}{l.first_pickup_ref ? ` (${l.first_pickup_ref})` : ''}</div>
                          <div style={{ fontSize: 12, color: 'var(--red)' }}>
                            {l.first_pickup_date ? new Date(l.first_pickup_date).toLocaleDateString() : ''} {l.first_pickup_time || ''}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      {l.last_delivery_location ? (
                        <>
                          <div>{l.last_delivery_location}{l.last_delivery_ref ? ` (${l.last_delivery_ref})` : ''}</div>
                          <div style={{ fontSize: 12, color: 'var(--red)' }}>
                            {l.last_delivery_date ? new Date(l.last_delivery_date).toLocaleDateString() : ''} {l.last_delivery_time || ''}
                          </div>
                        </>
                      ) : '—'}
                    </td>
                    <td>
                      <div className="rate-credit-status-col">
                        <div>
                          <span className="currency-rate" style={{ color: CURRENCY_COLORS[l.currency] || '#6B7280' }}>
                            {l.currency || 'CAD'} ${l.rate}
                          </span>
                        </div>
                        {Number(l.customer_credit_limit) > 0 && (() => {
                          // U = everything the customer currently owes (server:
                          // customerCreditUsed), same figure as the Order form.
                          const limit = Number(l.customer_credit_limit) || 0;
                          const used = Number(l.customer_credit_used) || 0;
                          const remaining = limit - used;
                          const money = (n) => `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString()}`;
                          return (
                            <div className="row-credit-line">
                              C: {money(limit)} &nbsp;U: {money(used)} &nbsp;R:{' '}
                              <span style={{ color: remaining >= 0 ? '#2a9350' : '#c4364b', fontWeight: 700 }}>
                                {money(remaining)}
                              </span>
                            </div>
                          );
                        })()}
                        <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span className={`badge ${l.status}`}>{l.status.replace('_',' ')}</span>
                          {l.order_status && (
                            <span className={`badge ${l.order_status}`}>
                              {l.order_status.charAt(0).toUpperCase() + l.order_status.slice(1)}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {/* Same icon buttons as the Stops table's Action column (CreateLoad.css) */}
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button type="button" className="icon-action-btn" title={`Edit order ${l.load_number}`} onClick={() => setEditLoadId(l.id)}>✎</button>
                        <button type="button" className="icon-action-btn danger" title={`Delete order ${l.load_number}`} onClick={() => deleteLoad(l)}>🗑</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div ref={footerRef}>
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                totalItems={loads.length}
                pageSize={rowsPerPage}
              />
            </div>
          </div>
          )}
        </div>

        {/* Always rendered — width animates via the .open class instead of
            mounting/unmounting instantly. */}
        <div className={'search-drawer-inline' + (showDrawer ? ' open' : '')}>
          <div className="search-drawer-header">
            <h3 style={{margin:0}}>Advanced search</h3>
            <button className="topbar-icon-btn" onClick={() => setShowDrawer(false)}>✕</button>
          </div>
          <div className="search-drawer-body">
            <label>Date from<br/>
              <input type="date" value={advanced.dateFrom} onChange={e => setAdvanced(a => ({...a, dateFrom: e.target.value}))} />
            </label>
            <label>Date to<br/>
              <input type="date" value={advanced.dateTo} onChange={e => setAdvanced(a => ({...a, dateTo: e.target.value}))} />
            </label>
            <label>Customer<br/>
              <select value={advanced.customer} onChange={e => setAdvanced(a => ({...a, customer: e.target.value}))}>
                <option value="">Select</option>
              </select>
            </label>
            <label>Pickup location<br/>
              <select value={advanced.pickup} onChange={e => setAdvanced(a => ({...a, pickup: e.target.value}))}>
                <option value="">Select</option>
              </select>
            </label>
            <label>Delivery location<br/>
              <select value={advanced.delivery} onChange={e => setAdvanced(a => ({...a, delivery: e.target.value}))}>
                <option value="">Select</option>
              </select>
            </label>
            <label>Load / order #<br/>
              <input value={advanced.orderNo} onChange={e => setAdvanced(a => ({...a, orderNo: e.target.value}))} />
            </label>
          </div>
          <div className="search-drawer-footer">
            <button className="primary-btn" style={{flex:1, justifyContent:'center'}}><Icon.search /> Search</button>
            <button onClick={resetAdvanced}>Reset</button>
          </div>
        </div>
      </div>
    </div>
  );
}
